import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useUpdateGroupMutation } from '@/api/v1';
import CheckIcon from '@/assets/icons/check.svg?react';
import CrossIcon from '@/assets/icons/close.svg?react';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { GroupFormFields } from '@/components/organisms/GroupFormFields';
import { Group } from '@/DTOs/groups';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { APPLICATION_FUNCTIONALITY, GroupFormData } from '@/schemas/group';

interface Props {
  readonly mode: 'edit' | 'view';
  readonly group: Group;

  onUpdated(): void;
}

const iconStyles = {
  check: { fill: '#4caf50', width: '1.2rem', height: '1.2rem' },
  cross: { fill: '#f44336', width: '1.2rem', height: '1.2rem' },
} as const;

export const TeamGroupDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { mode, group, onUpdated } = props;

  const form = useFormWithErrors<GroupFormData>({});
  const tr = useTranslator();
  const navigate = useNavigate();
  const ref = React.useRef<HTMLFormElement>(null);
  const [updateGroup] = useUpdateGroupMutation();

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit = React.useCallback(
    async (data: GroupFormData): Promise<void> => {
      try {
        const updateRequest = {
          groupId: group.groupId,
          groupName: data.groupName,
          description: data.description,
          roles: data.roles,
        };

        await updateGroup(updateRequest).unwrap();

        onUpdated();
      } catch (error) {
        console.error('Failed to update group:', error);
      }
    },
    [group.groupId, onUpdated, updateGroup],
  );

  const onCancel = React.useCallback((): void => {
    navigate('/settings/users/roles');
  }, [navigate]);

  React.useEffect((): void => {
    const { current } = ref;
    if (mode !== 'edit' || !current) {
      return;
    }

    current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Now nicely set focus on the first input :)
    const firstInput = current.querySelector('input');
    firstInput?.focus();
  }, [mode]);

  return (
    <>
      {mode === 'edit' ? (
        <Box component="form" ref={ref} sx={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <GroupFormFields form={form} initialValues={group} loading={isSubmitting} />

          <Box display="grid" gridTemplateColumns="1fr 1fr" width="75%" gap={2}>
            <LoadingButton
              variant="contained"
              size="compact"
              fullWidth={true}
              type="submit"
              loading={isSubmitting}>
              {tr('SAVE_CHANGES')}
            </LoadingButton>
            <Button
              variant="outlined"
              size="compact"
              fullWidth={true}
              disabled={isSubmitting}
              onClick={onCancel}>
              {tr('CANCEL')}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {group.description}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} pt={2} px={2}>
            {APPLICATION_FUNCTIONALITY.map((functionality: string): React.ReactElement => {
              const { roles } = group;

              return (
                <Box key={functionality} display="flex" alignItems="center" gap={1}>
                  {roles.includes(functionality) ? (
                    <CheckIcon style={iconStyles.check} />
                  ) : (
                    <CrossIcon style={iconStyles.cross} />
                  )}
                  <Typography variant="body2">{tr(functionality)}</Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  form: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    gap: 3,
    mt: 2,
  },
};
